import { Injectable } from '@angular/core';
import { Product } from '@app/models/product';
import { ApiService } from '@app/services/api/api.service';
import { ProductService } from '@app/services/api/product.service';
import { FlashService } from '@app/services/flash.service';
import {
  AddColor,
  BulkAlive,
  BulkAliveFail,
  BulkAliveSuccess,
  BulkDestroy,
  BulkDestroyFail,
  BulkDestroySuccess,
  BulkUpdate,
  BulkUpdateFail,
  BulkUpdateSuccess,
  CreateColor,
  CreateColorFail,
  CreateColorSuccess,
  DeletePictures,
  DeletePicturesSuccess,
  DeleteTechnicalFile,
  DeleteTechnicalFileFail,
  DeleteTechnicalFileSuccess,
  DestroyColor,
  DestroyColorFail,
  DestroyColorSuccess,
  DestroySingle,
  DestroySingleFail,
  DestroySingleSuccess,
  DownloadTechnicalFile,
  DownloadTechnicalFileFail,
  DownloadTechnicalFileSuccess,
  EmptyTrash,
  EmptyTrashFail,
  EmptyTrashSuccess,
  GetTechnicalFileLink,
  GetTechnicalFileLinkFail,
  LoadByCountryDepartment,
  LoadByCountryDepartmentFail,
  LoadByCountryDepartmentSuccess,
  LoadByFollow,
  LoadByFollowFail,
  LoadByFollowSuccess,
  LoadFail,
  LoadProductsForFoodShowroom,
  LoadProductsForFoodShowroomFail,
  LoadProductsForFoodShowroomSuccess,
  LoadSingle,
  LoadSingleFail,
  LoadSingleSuccess,
  LoadSuccess,
  MailProductsLikesMailForFoodShowroomProcessFail,
  MailProductsLikesMailForFoodShowroomProcessSuccess,
  OptimisticUpdateMany,
  OptimisticUpdateManyFail,
  OptimisticUpdateManySuccess,
  OptimisticUpdateOne,
  OptimisticUpdateOneFail,
  OptimisticUpdateOneSuccess,
  ProductActionTypes,
  ProductsPurchaseSummary,
  ProductsPurchaseSummarySuccess,
  ProductsTroncalsByCountries,
  ProductsTroncalsByCountriesFail,
  ProductsTroncalsByCountriesSuccess,
  RemoveColor,
  RestoreTrash,
  Search,
  SearchFail,
  SearchSuccess,
  SendProductsLikesMailForFoodShowroom,
  SendProductsLikesMailForFoodShowroomFail,
  SendProductsLikesMailForFoodShowroomProcessFail,
  SendProductsLikesMailForFoodShowroomProcessSuccess,
  SendProductsLikesMailForFoodShowroomSuccess,
  UpdateOneProductsForFoodShowroom,
  UpdateOneProductsForFoodShowroomFail,
  UpdateOneProductsForFoodShowroomSuccess,
  UploadTechnicalFile,
  UploadTechnicalFileFail,
  UpsertMany,
  UpsertProductsLikesForFoodShowroom,
  UpsertProductsLikesForFoodShowroomFail
} from '@core/actions/product.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  switchMap,
  tap
} from 'rxjs/operators';

import * as ProductActions from '../actions/product-country.actions';
import * as ProductShipmentActions from '../actions/product-shipment.actions';

@Injectable()
export class ProductEffects {
  @Effect()
  deleteTechnicalFile$ = this.actions$.pipe(
    ofType<DeleteTechnicalFile>(ProductActionTypes.DeleteTechnicalFile),
    map(action => [action.productId, action.followId]),
    switchMap(([productId, followId]) =>
        this.productsApi.deleteTechnicalFile(productId)
        .pipe(
          concatMap((res: any) => {
            return [new LoadProductsForFoodShowroom(followId)];
          }),
          catchError(error => of(new DeleteTechnicalFileFail(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  deleteTechnicalFileSuccess$ = this.actions$.pipe(
    ofType<DeleteTechnicalFileSuccess>(ProductActionTypes.DeleteTechnicalFileSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Eliminado con éxito' });
    })
  );
  
  @Effect()
  downloadTechnicalFile$ = this.actions$.pipe(
    ofType<DownloadTechnicalFile>(ProductActionTypes.DownloadTechnicalFile),
    map(action => [action.productId]),
    switchMap(([productId]) =>
        this.productsApi.downloadTechnicalFile(productId)
        .pipe(
          concatMap((res: any) => {
            return [new DownloadTechnicalFileSuccess(res)];
          }),
          catchError(error => of(new DownloadTechnicalFileFail(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  downloadTechnicalFileSuccess$ = this.actions$.pipe(
    ofType<DownloadTechnicalFileSuccess>(ProductActionTypes.DownloadTechnicalFileSuccess),
    map(action => {
      return this.productsApi.downloadWhishlist(action.data);
    })
  );

  @Effect()
  getTechnicalFileLink$ = this.actions$.pipe(
    ofType<GetTechnicalFileLink>(ProductActionTypes.GetTechnicalFileLink),
    map(action => [action.productId, action.file, action.fileableType, action.followId]),
    exhaustMap(([productId, file, fileableType, followId]) =>
      this.api.getTechnicalFileLink(productId, file, fileableType).pipe(
        concatMap((link: any) => {
          return [new UploadTechnicalFile(link, file, followId)];
        }),
        catchError(error => of(new GetTechnicalFileLinkFail(error)))
      )
    )
  );

  @Effect()
  uploadBanner$ = this.actions$.pipe(
    ofType<UploadTechnicalFile>(ProductActionTypes.UploadTechnicalFile),
    map(action => [action.link, action.file, action.productId]),
    exhaustMap(([link, file, followId]: [any, any, any]) => {
      const url = link.signedUrl;
      return this.api.uploadFile(url, file).pipe(
        concatMap(response => {
          return [new LoadProductsForFoodShowroom(followId)];
        }),
        catchError(e => {
          return of(new UploadTechnicalFileFail(e));
        })
      );
    })
  );



  @Effect({ dispatch: false })
  mailProductsLikesMailForFoodShowroomProcessSuccess$ = this.actions$.pipe(
    ofType<MailProductsLikesMailForFoodShowroomProcessSuccess>(ProductActionTypes.MailProductsLikesMailForFoodShowroomProcessSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: action.data.message });
    })
  );

  @Effect({ dispatch: false })
  mailProductsLikesMailForFoodShowroomProcessFail$ = this.actions$.pipe(
    ofType<MailProductsLikesMailForFoodShowroomProcessFail>(ProductActionTypes.MailProductsLikesMailForFoodShowroomProcessFail),
    map(action => {
      this.fs.emit({ type: 'danger', message: action.error.message });
    })
  );

  @Effect({ dispatch: false })
  sendProductsLikesMailForFoodShowroomProcessFail$ = this.actions$.pipe(
    ofType<SendProductsLikesMailForFoodShowroomProcessFail>(ProductActionTypes.SendProductsLikesMailForFoodShowroomProcessFail),
    map(action => {
      this.fs.emit({ type: 'danger', message: action.error.message });
    })
  );

  @Effect({ dispatch: false })
  sendProductsLikesMailForFoodShowroomProcessSuccess$ = this.actions$.pipe(
    ofType<SendProductsLikesMailForFoodShowroomProcessSuccess>(ProductActionTypes.SendProductsLikesMailForFoodShowroomProcessSuccess),
    map(action => {
      return this.productsApi.downloadWhishlist(action.data);
    })
  );

  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType(ProductActionTypes.Load),
    switchMap(() =>
      this.productsApi.get().pipe(
        map((products: Product[]) => new LoadSuccess(products)),
        catchError(error => of(new LoadFail(error)))
      )
    )
  );

  @Effect()
  createColor$: Observable<Action> = this.actions$.pipe(
    ofType<CreateColor>(ProductActionTypes.CreateColor),
    map(action => [action.productId, action.colorId]),
    switchMap(([productId, colorId]) =>
      this.api.createProductColor(productId, colorId).pipe(
        concatMap((res: any) => {
          return [new CreateColorSuccess(res), new AddColor(productId, res)];
        }),
        catchError(error => of(new CreateColorFail(error)))
      )
    )
  );

  @Effect()
  productsPurchaseSummary$: Observable<any> = this.actions$.pipe(
    ofType<ProductsPurchaseSummary>(ProductActionTypes.ProductsPurchaseSummary),
    map(action => [action.followId]),
    switchMap(([followId]) =>
      this.api.fetchProducts(followId).pipe(
        concatMap((res: any) => {
          return [new ProductsPurchaseSummarySuccess(res)];
        }),
        catchError(error => of(new CreateColorFail(error)))
      )
    )
  );

  @Effect()
  productsTroncalsByCountries$: Observable<any> = this.actions$.pipe(
    ofType<ProductsTroncalsByCountries>(ProductActionTypes.ProductsTroncalsByCountries),
    map(action => [action.followId]),
    switchMap(([followId]) =>
      this.api.fetchTroncals(followId).pipe(
        concatMap((res: any) => {
          return [new ProductsTroncalsByCountriesSuccess(res)];
        }),
        catchError(error => of(new ProductsTroncalsByCountriesFail(error)))
      )
    )
  );

  @Effect()
  destroyColor$: Observable<Action> = this.actions$.pipe(
    ofType<DestroyColor>(ProductActionTypes.DestroyColor),
    map(action => [action.ProductId, action.ColorId]),
    switchMap(([ProductId, ColorId]) =>
      this.api.removeProductColor(ProductId, ColorId).pipe(
        concatMap((res: any) => {
          return [
            new DestroyColorSuccess(res),
            new RemoveColor(ProductId, ColorId)
          ];
        }),
        catchError(error => of(new DestroyColorFail(error)))
      )
    )
  );

  @Effect()
  loadByFollow$: Observable<Action> = this.actions$.pipe(
    ofType<LoadByFollow>(ProductActionTypes.LoadByFollow),
    map(action => {
      // console.log('----', action);
      let opts: any = { FollowId: action.id };
      if (action.opts) {
        opts = { ...opts, ...action.opts };
      }
      return opts;
    }),
    switchMap(opts =>
      this.productsApi.get(opts).pipe(
        map(
          (products: Product[]) =>
            new LoadByFollowSuccess(products, opts.FollowId)
        ),
        catchError(error => of(new LoadByFollowFail(error)))
      )
    )
  );

  @Effect()
  loadSingle$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSingle>(ProductActionTypes.LoadSingle),
    map(action => action.id),
    switchMap(id => {
      return this.productsApi.getOne(id).pipe(
        switchMap((product: Product) => {
          const countryShipments = product.ProductCountries.reduce(
            (acc, item) => acc.concat(item.Shipments),
            []
          );

          return [
            new ProductShipmentActions.UpsertMany(countryShipments),
            new ProductActions.UpsertMany(product.ProductCountries),
            new LoadSingleSuccess(product)
          ];
        }),
        catchError(error => of(new LoadSingleFail(error)))
      );
    })
  );

  @Effect()
  optimisticUpdateOne$: Observable<Action> = this.actions$.pipe(
    ofType<OptimisticUpdateOne>(ProductActionTypes.OptimisticUpdateOne),
    map(action => [action.ProductId, action.state, action.newState]),
    concatMap(([id, prevState, newState]) =>
      this.productsApi.update(id, newState).pipe(
        map(product => new OptimisticUpdateOneSuccess(id, newState)),
        catchError(error =>
          of(new OptimisticUpdateOneFail(id, prevState, error))
        )
      )
    )
  );

  @Effect()
  optimisticUpdateMany$: Observable<Action> = this.actions$.pipe(
    ofType<OptimisticUpdateMany>(ProductActionTypes.OptimisticUpdateMany),
    map(action => [action.ProductIds, action.state, action.newState]),
    concatMap(([ProductIds, prevState, newState]) =>
      this.productsApi.bulkUpdate({ ProductIds, ...newState }).pipe(
        map(products => new OptimisticUpdateManySuccess(ProductIds, newState)),
        catchError(error =>
          of(new OptimisticUpdateManyFail(ProductIds, prevState, error))
        )
      )
    )
  );

  @Effect()
  bulkUpdate$: Observable<Action> = this.actions$.pipe(
    ofType<BulkUpdate>(ProductActionTypes.BulkUpdate),
    map(action => action.data),
    concatMap(data => {
      return this.productsApi.bulkUpdate(data).pipe(
        concatMap((products: Product[]) => {
          const message =
            products.length > 1
              ? `${products.length} Productos actualizados`
              : 'Producto actualizado';
          this.fs.emit({ type: 'success', message });
          return [new UpsertMany(products), new BulkUpdateSuccess(products)];
        }),
        catchError(error => of(new BulkUpdateFail(error)))
      );
    })
  );

  @Effect({ dispatch: false })
  BulkUpdateFail$ = this.actions$.pipe(
    ofType<BulkUpdateFail>(ProductActionTypes.BulkUpdateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        type: 'danger',
        message: res.error.error.message
      })
    )
  );

  @Effect()
  restoreTrash$: Observable<Action> = this.actions$.pipe(
    ofType<RestoreTrash>(ProductActionTypes.RestoreTrash),
    map(action => action.data),
    exhaustMap(data =>
      this.productsApi.restoreTrash(data.followId).pipe(
        concatMap(res => {
          this.fs.emit({
            type: 'success',
            message: 'La papelera se ha restaurado exitosamente'
          });
          return [new LoadByFollow(data.followId, { deletedAt: data.deletedAt })];
        }),
        catchError(error => of(new EmptyTrashFail(error)))
      )
    )
  );

  @Effect()
  emptyTrash$: Observable<Action> = this.actions$.pipe(
    ofType<EmptyTrash>(ProductActionTypes.EmptyTrash),
    map(action => action.FollowId),
    exhaustMap(FollowId =>
      this.productsApi.emptyTrash(FollowId).pipe(
        concatMap(res => {
          this.fs.emit({
            type: 'success',
            message: 'La papelera se ha vaciado exitosamente'
          });
          return [new EmptyTrashSuccess(FollowId)];
        }),
        catchError(error => of(new EmptyTrashFail(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  emptyTrashFail$ = this.actions$.pipe(
    ofType<EmptyTrashFail>(ProductActionTypes.EmptyTrashFail),
    map(action => action.error),
    tap(res =>
      console.log('action => ',res)
      // this.fs.emit({
      //   type: 'danger',
      //   message: res.error.error.message
      // })
    )
  );

  @Effect()
  destroySingle$: Observable<Action> = this.actions$.pipe(
    ofType<DestroySingle>(ProductActionTypes.DestroySingle),
    map(action => action.ProductId),
    exhaustMap(ProductId =>
      this.productsApi.destroySingle(ProductId).pipe(
        concatMap(res => {
          this.fs.emit({
            type: 'success',
            message: 'Producto eliminado exitosamente'
          });
          return [new DestroySingleSuccess(ProductId)];
        }),
        catchError(error => of(new DestroySingleFail(error)))
      )
    )
  );

  @Effect()
  search: Observable<Action> = this.actions$.pipe(
    ofType<Search>(ProductActionTypes.Search),
    map(action => [action.conditions, action.FollowId]),
    switchMap(([conditions, FollowId]) =>
      this.productsApi.search(conditions, FollowId).pipe(
        map((productIds: number[]) => new SearchSuccess(productIds)),
        catchError(error => of(new SearchFail(error)))
      )
    )
  );

  @Effect()
  bulkDestroy$: Observable<Action> = this.actions$.pipe(
    ofType<BulkDestroy>(ProductActionTypes.BulkDestroy),
    concatMap(action =>
      this.productsApi.bulkDestroy(action.FollowId, action.ProductIds).pipe(
        concatMap((res: any) => {
          this.fs.emit({
            type: 'success',
            message: 'Producto(s) eliminado(s)'
          });
          return [new BulkDestroySuccess(action.ProductIds, res)];
        }),
        catchError(error => of(new BulkDestroyFail(error)))
      )
    )
  );

  @Effect()
  bulkAlive$: Observable<any> = this.actions$.pipe(
    ofType<BulkAlive>(ProductActionTypes.BulkAlive),
    concatMap(action =>
      this.productsApi.bulkAlive(action.FollowId, action.ProductId).pipe(
        concatMap((res: any) => {
          this.fs.emit({ type: 'success', message: 'Producto restaurado' });
          // new LoadByFollow(action.FollowId, { deletedAt: true });
          return [new BulkAliveSuccess(action.ProductId, res)];
        }),
        catchError(error => of(new BulkAliveFail(error)))
      )
    )
  );

  // @Effect()
  // bulkAlive$: Observable<Action> = this.actions$.pipe(
  //   ofType<BulkAlive>(ProductActionTypes.BulkAlive),
  //   switchMap(action =>
  //     this.productsApi.bulkAlive(action.FollowId, action.ProductId).pipe(
  //       map(
  //         (aliveProduct: Product) =>
  //           new BulkAliveSuccess(aliveProduct, {
  //             FollowId: action.FollowId
  //           })
  //       ),
  //       catchError(error => of(new BulkAliveFail(error)))
  //     )
  //   )
  // );

  @Effect()
  bulkAliveSuccess$ = this.actions$.pipe(
    ofType<BulkAliveSuccess>(ProductActionTypes.BulkAliveSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Producto restaurado' });
      return new LoadByFollow(action.data.FollowId, { deletedAt: true });
    })
  );

  // @Effect()
  // bulkAliveSuccess$ = this.actions$.pipe(
  //   ofType<BulkAliveSuccess>(ProductActionTypes.BulkAliveSuccess),
  //   map(action => action.ProductId),
  //    {    }
  // );

  @Effect({ dispatch: false })
  bulkAliveFail$ = this.actions$.pipe(
    ofType<BulkAliveFail>(ProductActionTypes.BulkAliveFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        type: 'danger',
        message: res.error.message
      })
    )
  );

  @Effect()
  loadProductsForFoodShowroom$ = this.actions$.pipe(
    ofType<LoadProductsForFoodShowroom>(ProductActionTypes.LoadProductsForFoodShowroom),
    map(action => [
      action.followId,
    ]),
    switchMap(([followId]) =>
        this.productsApi.getProductsForFoodShowroom(followId)
          .pipe(map(
              (products: any) => new LoadProductsForFoodShowroomSuccess(products)),
            catchError(error => of(new LoadProductsForFoodShowroomFail(error)))
          )
    )
  );

  @Effect()
  updateOneProductsForFoodShowroom$ = this.actions$.pipe(
    ofType<UpdateOneProductsForFoodShowroom>(ProductActionTypes.UpdateOneProductsForFoodShowroom),
    map(action => [
      action.productId, action.claims,
    ]),
    switchMap(([productId, claims]) =>
        this.productsApi.updateOneProductsForFoodShowroom(productId, claims)
        .pipe(
          concatMap((res: any) => {
            if(res && res.message){
              this.fs.emit({ type: 'danger', message: res.message });

            }else{
              this.fs.emit({ type: 'success', message: 'Claims actualizado' });
              return [new LoadProductsForFoodShowroom(res.FollowId)];
            }
          }),
          catchError(error => of(new UpdateOneProductsForFoodShowroomFail(error)))
        )
    )
  );

  @Effect()
  sendProductsLikesMailForFoodShowroom$ = this.actions$.pipe(
    ofType<SendProductsLikesMailForFoodShowroom>(ProductActionTypes.SendProductsLikesMailForFoodShowroom),
    map(action => [action.data]),
    switchMap(([data]) =>
        this.productsApi.sendProductsLikesMailForFoodShowroom(data)
        .pipe(
          concatMap((res: any) => {
            return [new SendProductsLikesMailForFoodShowroomSuccess(res.data)];
          }),
          catchError(error => of(new SendProductsLikesMailForFoodShowroomFail(error)))
        )
    )
  );

  @Effect()
  upsertProductsLikesForFoodShowroom$ = this.actions$.pipe(
    ofType<UpsertProductsLikesForFoodShowroom>(ProductActionTypes.UpsertProductsLikesForFoodShowroom),
    map(action => [
      action.data,
    ]),
    switchMap(([data]) =>
        this.productsApi.upsertProductsLikesForFoodShowroom(data)
        .pipe(
          concatMap((res: any) => {
            return [new UpdateOneProductsForFoodShowroomSuccess(res.data)];
          }),
          catchError(error => of(new UpsertProductsLikesForFoodShowroomFail(error)))
        )
    )
  );

  @Effect()
  loadByCountryDepartment$ = this.actions$.pipe(
    ofType<LoadByCountryDepartment>(ProductActionTypes.LoadByCountryDepartment),
    map(action => [
      action.FollowId,
      action.countryDepartmentId,
      action.CountryId,
      action.businessUnit,
      action.typeHierarchy,
      action.DestinationCenterId
    ]),
    switchMap(
      ([
        FollowId,
        countryDepartmentId,
        CountryId,
        businessUnit,
        typeHierarchy,
        DestinationCenterId
      ]) =>
        this.productsApi
          .getByCountryDepartment(
            FollowId,
            countryDepartmentId,
            CountryId,
            businessUnit,
            typeHierarchy,
            DestinationCenterId
          )
          .pipe(
            map(
              (products: Product[]) =>
                new LoadByCountryDepartmentSuccess(products)
            ),
            catchError(error => of(new LoadByCountryDepartmentFail(error)))
          )
    )
  );

  @Effect()
  DeletePictures$ = this.actions$.pipe(
    ofType<DeletePictures>(ProductActionTypes.DeletePictures),
    map(action => [action.file, action.productId]),
    exhaustMap(([file, productId]) => {
      this.fs.emit({ type: 'success', message: 'Imagen eliminada' });
      return this.api.deleteFile(file).pipe(
        concatMap(response => {
          return [new DeletePicturesSuccess(productId)];
        })
      );
    })
  );

  @Effect()
  DeletePicturesSuccess$ = this.actions$.pipe(
    ofType<DeletePicturesSuccess>(ProductActionTypes.DeletePicturesSuccess),
    map(action => {
      return new LoadSingle(action.productId);
    })
  );

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private productsApi: ProductService,
    private fs: FlashService
  ) {}
}
